import { useCallback, useEffect, useState } from 'react';
import { MoreHorizontal, Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import { DECLINE_REASONS, requiresDetail, declineLabels } from '@/lib/declineReasons.js';
import {
  MARKET_CHANNEL_OPTIONS, CHANNEL_PROMPT, CHANNEL_UNSELECTED,
  channelForCarrier, wholesalerForCarrier, needsWholesaler, wholesalerOptions, otherWholesalerOptions,
} from '@/lib/marketChannel.js';
import { marketsOnly } from '@/lib/carrierRoles.js';
import CrmContactPicker from '@/components/licenses/CrmContactPicker.jsx';
import { searchProducers } from '@/lib/producerSearch.js';
import NudgeSheet from '@/components/sheets/NudgeSheet.jsx';
import { toDate } from '@/lib/date-display.js';
import {
  listDealMarkets,
  setMarketState,
  acceptMarketSuggestion,
  dismissMarketSuggestion,
  createManualMarket,
  recordKnownDecline,
  listCarrierOptions,
} from '@/services/markets.service.js';

// MV-02 2.3 (Ship 2 item 1): the market state ladder — one row per market the
// deal was dispatched to, one glance: state, age, last event. Manual state set
// lives in the row menu (the ONLY thing that moves state); inbound-derived
// suggestions render as a chip with one-click Accept / Dismiss.

const STATE_BADGE = {
  sent: { label: 'Sent', variant: 'outline' },
  acknowledged: { label: 'Acknowledged', variant: 'info' },
  working: { label: 'Working', variant: 'info' },
  quoted: { label: 'Quoted', variant: 'success' },
  declined: { label: 'Declined', variant: 'muted' },
  blocked: { label: 'Blocked', variant: 'warning' },
  no_response: { label: 'No response', variant: 'warning' },
};

// TWO KINDS OF NO, ON THE SAME LADDER, TOLD APART (CC PROMPT 23 Part A). David's
// call: one place to look, never claiming a send that did not happen.
//
//   reply  they answered a submission we made
//   known  the option was not selectable, so there was no submission at all:
//          "if it's not selectable, it's a decline. there's no 'submission' -
//          it's just an understood outcome"
//
// A known-decline row must never render "Sent 3d ago". That single line is the
// false claim this whole distinction exists to prevent.
function badgeFor(market) {
  if (market.state === 'declined' && market.decline_basis === 'known') {
    return { label: "Won't write", variant: 'muted' };
  }
  return STATE_BADGE[market.state] || { label: market.state, variant: 'default' };
}

// Direct one-click state sets. Declined and Blocked need their capture
// dialogs (structured reason / who holds the account) — wired separately.
const QUICK_STATES = ['acknowledged', 'working', 'quoted', 'no_response'];

// Structured declination vocabulary (MV-02 item 2) — mirrors the server enum.
// These are knowledgebase fuel: the learned pre-check scores them against
// future risk shapes, so a reason is REQUIRED to mark declined.
export function DeclineDialog({ market, open, onOpenChange, onChanged }) {
  const [reasons, setReasons] = useState([]);
  const [detail, setDetail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReasons([]);
      setDetail('');
    }
  }, [open]);

  const toggleReason = (v) =>
    setReasons((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  // Detail is required for 'other' ONLY, never for not_stated: there is nothing
  // to explain about a silence.
  const canSave = !saving && reasons.length > 0
    && (!requiresDetail(reasons) || detail.trim().length > 0);

  async function save() {
    setSaving(true);
    try {
      await setMarketState(market.id, { state: 'declined', declination: { reasons, detail: detail.trim() || null } });
      toast.success('Marked declined');
      onOpenChange(false);
      onChanged?.();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Decline failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Decline: {market?.carrier_name || 'this market'}</DialogTitle>
          <DialogDescription>
            Why did they pass? This feeds the market suggestions on future risks.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          {DECLINE_REASONS.map((r) => (
            <label key={r.value} className="flex cursor-pointer items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={reasons.includes(r.value)} onChange={() => toggleReason(r.value)} />
              {r.label}
            </label>
          ))}
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            rows={2}
            placeholder={requiresDetail(reasons) ? 'Detail (required for Other)' : 'Detail (optional): what exactly did they say?'}
            className="mt-2 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!canSave}>{saving ? 'Saving…' : 'Mark declined'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Blocked capture (MV-02 item 4): who holds the account (required) and until
// when (optional). The row joins the account-level blocked registry that
// MV-09's clearing play reads and item 5's suggestions exclude.
export function BlockDialog({ market, open, onOpenChange, onChanged }) {
  const [blockedBy, setBlockedBy] = useState('');
  const [blockedUntil, setBlockedUntil] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setBlockedBy('');
      setBlockedUntil('');
    }
  }, [open]);

  const canSave = !saving && blockedBy.trim().length > 0;

  async function save() {
    setSaving(true);
    try {
      await setMarketState(market.id, {
        state: 'blocked',
        blocked_by: blockedBy.trim(),
        blocked_until: blockedUntil || null,
      });
      toast.success('Marked blocked');
      onOpenChange(false);
      onChanged?.();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Block failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Blocked: {market?.carrier_name || 'this market'}</DialogTitle>
          <DialogDescription>
            Another broker holds this account at the market. Active blocks are excluded from future suggestions and feed the renewal clearing play.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-xs font-medium text-text-muted">Who holds the account (required)</p>
            <input
              value={blockedBy}
              onChange={(e) => setBlockedBy(e.target.value)}
              placeholder="Broker or agency name"
              className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-text-muted">Blocked until (optional)</p>
            <input
              type="date"
              value={blockedUntil}
              onChange={(e) => setBlockedUntil(e.target.value)}
              className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!canSave}>{saving ? 'Saving…' : 'Mark blocked'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// THE SIGNAL IS A HINT, NOT A GATE (CC PROMPT 23 Part C). David: "I don't want
// TRACON playing broker, I want it providing helpful hints and feedback. so I
// don't want it to decide unilaterally that something can't happen."
//
// Red and yellow differ in KIND, not degree, and the chip has to say which:
//   red     a rule David recorded matched this risk. A FACT.
//   yellow  this carrier has already said no on this account. A HUNCH.
//   green   no negative signal. NOT a yes, and the copy must not imply one.
//
// Every carrier stays selectable in every state. Nothing here is ever disabled.
const SIGNAL_CHIP = {
  red: { dot: 'bg-danger', text: 'text-danger', word: 'rule' },
  yellow: { dot: 'bg-warning', text: 'text-warning', word: 'history' },
  green: { dot: 'bg-border-strong', text: 'text-text-dim', word: '' },
};

function SignalNote({ entry }) {
  if (!entry || entry.signal === 'green') return null;
  const tone = SIGNAL_CHIP[entry.signal] || SIGNAL_CHIP.green;
  return (
    <div className={`mt-1.5 rounded border border-border bg-bg px-2.5 py-1.5 text-[11px] ${tone.text}`}>
      {(entry.reasons || []).map((r, i) => (
        <p key={i} className="flex items-start gap-1.5">
          <span className={`mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${tone.dot}`} />
          <span>
            {r.label}
            {/* The remark that produced the rule, so a six-week-old hint reads
                as a remark rather than as the carrier's underwriting guide. */}
            {r.source?.said ? <span className="block text-text-dim">{r.source.said}</span> : null}
            {r.detail ? <span className="block text-text-dim">{r.detail}</span> : null}
          </span>
        </p>
      ))}
      <p className="mt-1 text-text-dim">
        {entry.signal === 'red'
          ? 'A rule on file says they will not write this. You can still send it.'
          : 'They have passed on this account before. Nothing stops you sending it.'}
      </p>
    </div>
  );
}

// Log a submission (WALKTHROUGH fix 4): the broker made a submission outside a
// dispatch (a carrier portal, a direct email). Pick the carrier, add an
// optional note; a 'sent' row is born so it can be marked quoted or declined.
export function LogSubmissionDialog({ dealId, open, onOpenChange, onChanged }) {
  const [carriers, setCarriers] = useState([]);
  const [slug, setSlug] = useState('');
  const [note, setNote] = useState('');
  // THE CHANNEL IS REQUIRED (prompt 67 item 2.1). It used to offer "I would
  // rather not say" AS THE DEFAULT, so every submission logged without a
  // deliberate change recorded an unknown transport. David: "I'd MURDER AN
  // EMPLOYEE if they told me that. Not an option. This isn't a survey, it's a
  // business process."
  //
  // This does NOT undo CC PROMPT 30 Part B1. That fixed the code ASSUMING
  // 'portal' server-side, which is how the Jennings row claimed a portal David
  // has no access to. Refusing to file the row until he states a transport is
  // the opposite failure mode: nothing is guessed, the answer is demanded.
  // '' is the unselected sentinel and can never be submitted.
  const [channel, setChannel] = useState(CHANNEL_UNSELECTED);
  const [saving, setSaving] = useState(false);
  const [riskKnown, setRiskKnown] = useState(true);

  useEffect(() => {
    if (!open) return;
    setSlug('');
    setNote('');
    setChannel(CHANNEL_UNSELECTED);
    setProducer(null);
    listCarrierOptions(dealId)
      .then((d) => { setCarriers(d.carriers || []); setRiskKnown(d.risk_known !== false); })
      .catch(() => { setCarriers([]); setRiskKnown(true); });
  }, [open, dealId]);

  const selected = carriers.find((c) => c.slug === slug) || null;

  // THE HANDLE FOR A DOOR THAT ALREADY EXISTS. recordSend takes
  // submitted_to_slug and Rokstone on N5461P carries `bwi`, but this dialog —
  // the door David actually uses — offered "Through a wholesaler" with nowhere
  // to say WHICH. "Through a wholesaler" is an answer that identifies nothing,
  // a placeholder wearing a channel's clothes.
  //
  // REQUIRED when the channel is cobroker, because that is the one channel
  // whose meaning is incomplete without it. carrier_slug stays the UNDERWRITER;
  // this is who carried it.
  const [viaSlug, setViaSlug] = useState('');
  // WHO PRODUCED IT, asked beside the wholesaler because that is where it is
  // known (David 2026-09-03). Per market, carrier to carrier. { id, name } | null.
  const [producer, setProducer] = useState(null);
  // THE METHOD PREFILLS FROM THE CARRIER. routing_type is the per-carrier
  // default channel and maps onto the channel enum; picking a carrier answers
  // the question instead of asking it. David: "the How did you send it is Pick
  // how it went — all that is awkward." Most of that awkwardness is the field
  // being asked at all when the carrier already implies the answer. Still
  // overridable: the select stays, this only seeds it.
  //
  // THE RULE ITSELF LIVES IN lib/marketChannel.js, shared with the phantom row
  // (prompt 93). It used to be declared here, and a second copy on the new door
  // would have been the same question answered in two files: they would agree
  // on the common case and disagree the day a routing_type changed.
  function pickCarrier(nextSlug) {
    setSlug(nextSlug);
    const c = carriers.find((x) => x.slug === nextSlug);
    const seeded = channelForCarrier(c);
    if (seeded) setChannel(seeded);
    // A carrier routed via a wholesaler usually goes via THAT wholesaler.
    const via = wholesalerForCarrier(c);
    if (via) setViaSlug(via);
  }
  const needsVia = needsWholesaler(channel);
  const canSave = !saving && !!slug && !!channel && (!needsVia || !!viaSlug);

  async function save() {
    setSaving(true);
    try {
      await createManualMarket(dealId, {
        carrier_slug: slug,
        note: note.trim() || null,
        channel,
        // Only sent when the channel means it. A wholesaler recorded against a
        // direct email would be a second fact nobody stated.
        submitted_to_slug: needsVia ? viaSlug : null,
        // Who produced it, when a wholesaler carried it. Blank means NDEMNIFLY.
        producer_contact_id: needsVia && producer?.id ? String(producer.id) : null,
      });
      toast.success('Submission logged');
      onOpenChange(false);
      onChanged?.();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Could not log the submission');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log a submission</DialogTitle>
          <DialogDescription>
            Record a submission you made outside a dispatch (a carrier portal, a direct email). It joins the ladder so you can mark it quoted or declined.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-xs font-medium text-text-muted">Carrier (required)</p>
            <select
              value={slug}
              onChange={(e) => pickCarrier(e.target.value)}
              className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
            >
              <option value="">Pick a carrier</option>
              {/* Ranked most-likely first. NEVER disabled: a carrier with a rule
                  against it sinks to the bottom and says why, and the broker
                  picks it anyway if he knows better. */}
              {/* MARKETS ONLY (prompt 97 item 1). A wholesaler is the answer to
                  "how did you send it" and belongs in that field; paper is whose
                  policy it is and belongs nowhere near a submission. Neither is a
                  market being hidden from him. Same predicate as the phantom row.
                  Appetite still never filters: red and yellow annotate and sort. */}
              {marketsOnly(carriers).map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}{c.signal === 'red' ? '  (rule matched)' : c.signal === 'yellow' ? '  (declined before)' : ''}
                </option>
              ))}
            </select>
            <SignalNote entry={selected} />
            {!riskKnown && (
              <p className="mt-1.5 text-[11px] text-text-dim">
                Nothing is entered on this deal yet, so no carrier has been checked against the risk.
              </p>
            )}
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-text-muted">How did you send it? (required)</p>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
            >
              {/* The empty option is the UNSELECTED state, not an answer. It
                  cannot be saved (canSave requires a channel) and the server
                  400s on it independently. Vocabulary shared with the phantom
                  row so the two doors cannot drift. */}
              <option value={CHANNEL_UNSELECTED}>{CHANNEL_PROMPT}</option>
              {MARKET_CHANNEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-text-dim">
              How a submission reached the carrier is part of the record, not an optional remark.
            </p>
          </div>
          {needsVia ? (
            <div>
              <p className="mb-1 text-xs font-medium text-text-muted">Which wholesaler? (required)</p>
              <select
                value={viaSlug}
                onChange={(e) => setViaSlug(e.target.value)}
                className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
              >
                {/* COBROKERS LEAD. This offered the entire carrier list flat, so
                    an underwriter could be named as the wholesaler that carried a
                    submission to itself. is_cobroker sorts it; it does not gate
                    it (prompt 94 item 1), and only the underwriter named above is
                    actually removed. */}
                <option value="">Pick who carried it</option>
                <optgroup label="Wholesalers">
                  {wholesalerOptions(carriers).map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name || c.slug}</option>
                  ))}
                </optgroup>
                <optgroup label="Everyone else">
                  {otherWholesalerOptions(carriers, slug).map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name || c.slug}</option>
                  ))}
                </optgroup>
              </select>
              <p className="mt-1 text-[11px] text-text-dim">
                The carrier above is who writes the paper. This is who carried the submission to them.
              </p>
              <div className="mt-3">
                <p className="mb-1 text-xs font-medium text-text-muted">Producer at the wholesaler (optional)</p>
                <CrmContactPicker value={producer} searchFn={searchProducers} placeholder="Search producers…" onSelect={setProducer} />
                <p className="mt-1 text-[11px] text-text-dim">
                  Who produced it. Sets the brand on the quote paperwork for this market: an IBI producer prints IBI, blank prints NDEMNIFLY.
                </p>
              </div>
            </div>
          ) : null}
          <div>
            <p className="mb-1 text-xs font-medium text-text-muted">Note (optional)</p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Anything to remember about this submission"
              className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!canSave}>{saving ? 'Saving…' : 'Log submission'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Record a known decline: the carrier's answer arrived through the absence of
// the option, so there is no submission to log. Deliberately a SEPARATE door
// from "Log a submission" rather than a checkbox inside it, because the two
// produce different records and a checkbox is one mis-click from putting a send
// that never happened into the shopping history.
//
// The carrier picker here is the ranked one, which is the point: a red chip
// ("1973 Piper predates their 1990 floor") is usually the exact reason the
// broker opened this dialog.
export function KnownDeclineDialog({ dealId, open, onOpenChange, onChanged }) {
  const [carriers, setCarriers] = useState([]);
  const [slug, setSlug] = useState('');
  const [reasons, setReasons] = useState([]);
  const [detail, setDetail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSlug(''); setReasons([]); setDetail('');
    listCarrierOptions(dealId).then((d) => setCarriers(d.carriers || [])).catch(() => setCarriers([]));
  }, [open, dealId]);

  const selected = carriers.find((c) => c.slug === slug) || null;
  const canSave = !saving && !!slug && reasons.length > 0
    && (!requiresDetail(reasons) || detail.trim().length > 0);

  async function save() {
    setSaving(true);
    try {
      await recordKnownDecline(dealId, { carrier_slug: slug, reasons, detail: detail.trim() || null });
      toast.success('Recorded');
      onOpenChange(false);
      onChanged?.();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Could not record it');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>They won&apos;t write it</DialogTitle>
          <DialogDescription>
            For a market whose answer you already know, where there is nothing to submit. It joins the
            ladder as their answer, and it will not claim a submission you did not make.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-xs font-medium text-text-muted">Carrier (required)</p>
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
            >
              <option value="">Pick a carrier</option>
              {/* MARKETS ONLY (prompt 97 item 1). A wholesaler is the answer to
                  "how did you send it" and belongs in that field; paper is whose
                  policy it is and belongs nowhere near a submission. Neither is a
                  market being hidden from him. Same predicate as the phantom row.
                  Appetite still never filters: red and yellow annotate and sort. */}
              {marketsOnly(carriers).map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}{c.signal === 'red' ? '  (rule matched)' : c.signal === 'yellow' ? '  (declined before)' : ''}
                </option>
              ))}
            </select>
            <SignalNote entry={selected} />
          </div>
          <fieldset className="space-y-1.5">
            <legend className="mb-1 text-xs font-medium text-text-muted">Why (required)</legend>
            {DECLINE_REASONS.map((r) => (
              <label key={r.value} className="flex cursor-pointer items-center gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  checked={reasons.includes(r.value)}
                  onChange={() => setReasons((prev) => prev.includes(r.value) ? prev.filter((x) => x !== r.value) : [...prev, r.value])}
                />
                {r.label}
              </label>
            ))}
            <textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              rows={2}
              placeholder={requiresDetail(reasons) ? 'Detail (required for Other)' : 'Detail (optional)'}
              className="mt-2 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </fieldset>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!canSave}>{saving ? 'Saving…' : 'Record it'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ageDays(iso) {
  if (!iso) return null;
  const then = toDate(iso)?.getTime() ?? NaN;
  if (isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86400000));
}

function agoLabel(iso) {
  const d = ageDays(iso);
  if (d == null) return '';
  if (d === 0) return 'today';
  return `${d}d ago`;
}

function lastEvent(market) {
  const h = market.state_history || [];
  const last = h[h.length - 1];
  if (!last) return null;
  if (last.event === 'suggestion_dismissed') return { label: `suggestion dismissed`, at: last.at };
  if (last.event === 'nudge_sent') return { label: 'follow-up sent', at: last.at };
  return { label: STATE_BADGE[last.state]?.label?.toLowerCase() || last.state, at: last.at };
}

export function MarketRow({ market, onChanged, extraChips = null, menuExtras = null }) {
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const badge = badgeFor(market);
  const knownDecline = market.state === 'declined' && market.decline_basis === 'known';
  // AN INFERRED ROW MUST NOT LOOK LIKE ONE DAVID RECORDED (CC PROMPT 37 Part C).
  // The ladder backfill created 226 rows from the fact that a quote came back,
  // which is sound evidence a submission happened and is NOT the same as having
  // watched it go out. Same treatment as a known decline: one place to look,
  // never claiming something it cannot support. No new vocabulary.
  const inferredSend = (market.state_history || []).some((h) => h && h.inferred_from === 'quote');
  const ev = lastEvent(market);
  const recipients = (market.recipients || []).map((r) => r.name || r.email).filter(Boolean);
  const title = market.carrier_name || recipients.join(', ') || 'Ad-hoc';

  async function act(fn, okMsg) {
    setBusy(true);
    try {
      await fn();
      okMsg && toast.success(okMsg);
      onChanged?.();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/60 py-2 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text">
          {title}
          {/* No channel on a known decline, because there was no transport. */}
          {market.channel
            ? <span className="ml-2 text-[11px] uppercase tracking-wide text-text-dim">{market.channel}</span>
            : knownDecline
              ? <span className="ml-2 text-[11px] uppercase tracking-wide text-text-dim">not submitted</span>
              : inferredSend
                ? <span className="ml-2 text-[11px] uppercase tracking-wide text-text-dim" title="No transport recorded. This row exists because a quote came back, which means it was sent.">inferred</span>
                : null}
          {market.clearing && <span className="ml-2 text-[11px] text-warning">clearing</span>}
        </p>
        <p className="text-[11px] text-text-muted">
          {knownDecline
            ? `Recorded ${agoLabel(market.created_at)}`
            /* "Quote came back" states what is actually known. "Sent" would
               claim an observation nobody made. */
            : inferredSend
              ? `Quote came back ${agoLabel(market.created_at)}`
              : `Sent ${agoLabel(market.created_at)}`}
          {ev && ev.at !== market.created_at ? ` · last: ${ev.label} ${agoLabel(ev.at)}` : ''}
          {market.state === 'declined' && declineLabels(market.declination_reason).length
            ? ` · ${declineLabels(market.declination_reason).join(', ')}${market.declination_reason.detail ? `: ${market.declination_reason.detail}` : ''}`
            : ''}
          {market.state === 'blocked' && market.blocked_by
            ? ` · held by ${market.blocked_by}${market.blocked_until ? ` until ${market.blocked_until}` : ''}`
            : ''}
        </p>
      </div>

      {extraChips}

      {/* Nudge chip (item 3): due-ness derived server-side from state +
          timestamps. Click opens the follow-up draft; broker sends. */}
      {market.nudge?.due && (
        <button
          onClick={() => setNudgeOpen(true)}
          className="rounded-pill bg-warning-soft px-2 py-0.5 text-[11px] text-warning hover:bg-warning/20"
          title={`Quiet ${market.nudge.days_quiet}d (threshold ${market.nudge.threshold_days}d). Draft a follow-up.`}
        >
          Follow up · {market.nudge.days_quiet}d quiet
        </button>
      )}

      {market.suggested_state && (
        <span className="flex items-center gap-1 rounded-pill bg-info-soft px-2 py-0.5 text-[11px] text-info">
          Suggested: {STATE_BADGE[market.suggested_state]?.label || market.suggested_state}
          {market.suggested_source ? ` (${market.suggested_source.replace(/_/g, ' ')})` : ''}
          <button
            disabled={busy}
            title="Accept"
            className="ml-1 rounded p-0.5 hover:bg-info/20"
            onClick={() => act(() => acceptMarketSuggestion(market.id), 'Suggestion accepted')}
          >
            <Check size={12} />
          </button>
          <button
            disabled={busy}
            title="Dismiss"
            className="rounded p-0.5 hover:bg-info/20"
            onClick={() => act(() => dismissMarketSuggestion(market.id), 'Suggestion dismissed')}
          >
            <X size={12} />
          </button>
        </span>
      )}

      {/* One-click Quoted / Declined on any 'sent' row (WALKTHROUGH fix 4).
          Declined keeps the required-reason dialog. */}
      {market.state === 'sent' && (
        <>
          <button
            disabled={busy}
            onClick={() => act(() => setMarketState(market.id, { state: 'quoted' }), 'Marked quoted')}
            className="rounded-pill bg-success-soft px-2 py-0.5 text-[11px] text-success hover:bg-success/20"
          >
            Quoted
          </button>
          <button
            disabled={busy}
            onClick={() => setDeclineOpen(true)}
            className="rounded-pill bg-warning-soft px-2 py-0.5 text-[11px] text-warning hover:bg-warning/20"
          >
            Declined
          </button>
        </>
      )}

      <Badge variant={badge.variant}>{badge.label}</Badge>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" disabled={busy} className="h-7 w-7 p-0">
            <MoreHorizontal size={15} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {QUICK_STATES.filter((s) => s !== market.state).map((s) => (
            <DropdownMenuItem
              key={s}
              onSelect={() => act(() => setMarketState(market.id, { state: s }), `Marked ${STATE_BADGE[s].label.toLowerCase()}`)}
            >
              Mark {STATE_BADGE[s].label.toLowerCase()}
            </DropdownMenuItem>
          ))}
          {market.state !== 'declined' && (
            <DropdownMenuItem onSelect={() => setDeclineOpen(true)}>Mark declined…</DropdownMenuItem>
          )}
          {market.state !== 'blocked' && (
            <DropdownMenuItem onSelect={() => setBlockOpen(true)}>Mark blocked…</DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setNudgeOpen(true)}>Draft follow-up…</DropdownMenuItem>
          {menuExtras}
        </DropdownMenuContent>
      </DropdownMenu>

      <DeclineDialog market={market} open={declineOpen} onOpenChange={setDeclineOpen} onChanged={onChanged} />
      <BlockDialog market={market} open={blockOpen} onOpenChange={setBlockOpen} onChanged={onChanged} />
      <NudgeSheet marketId={market.id} open={nudgeOpen} onOpenChange={setNudgeOpen} onSent={onChanged} />
    </div>
  );
}

export default function MarketLadderPanel({ dealId, refreshKey, renderRow }) {
  const [markets, setMarkets] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [knownOpen, setKnownOpen] = useState(false);

  const refresh = useCallback(() => {
    if (!dealId) return;
    listDealMarkets(dealId)
      .then((d) => setMarkets(d.markets || []))
      .catch(() => setMarkets(null));
  }, [dealId]);

  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);

  // Still loading (or errored) — no flash. An empty array still renders, so the
  // "Log a submission" door is reachable before any formal dispatch exists.
  if (!markets) return null;

  return (
    <div className="rounded-card border border-border bg-surface px-3.5 py-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-dim">
          {markets.length ? `Markets · ${markets.length}` : 'Markets'}
        </p>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setLogOpen(true)}
            className="rounded-pill bg-bg px-2 py-0.5 text-[11px] font-medium text-text-muted hover:text-text"
          >
            + Log a submission
          </button>
          <button
            onClick={() => setKnownOpen(true)}
            className="rounded-pill bg-bg px-2 py-0.5 text-[11px] font-medium text-text-muted hover:text-text"
            title="A market whose answer you already know, with nothing to submit"
          >
            + They won&apos;t write it
          </button>
        </div>
      </div>
      {markets.length === 0 && (
        <p className="text-[11px] text-text-muted">Nothing on the ladder yet. Log a submission you made in a carrier portal, or record a market that will not write it.</p>
      )}
      {markets.map((m) =>
        renderRow ? renderRow(m, refresh) : <MarketRow key={m.id} market={m} onChanged={refresh} />
      )}
      <LogSubmissionDialog dealId={dealId} open={logOpen} onOpenChange={setLogOpen} onChanged={refresh} />
      <KnownDeclineDialog dealId={dealId} open={knownOpen} onOpenChange={setKnownOpen} onChanged={refresh} />
    </div>
  );
}
